import type {
  MarginCenterCustomerProductSeriesPoint,
} from '../types/marginCenter';

import type {
  MarginCenterEconomicMetricKey,
  MarginCenterEconomicReading,
} from './customerProductEconomicReadings';

export type RelationshipSeriesPoint =
  MarginCenterCustomerProductSeriesPoint;

export interface RelationshipAlignedBucket {
  index: number;
  current: RelationshipSeriesPoint;
  comparison: RelationshipSeriesPoint;
  primaryDifference: number | null;
  secondaryDifference: number | null;
}

export interface RelationshipDirectionCounts {
  bothRise: number;
  bothFall: number;
  opposite: number;
  unchangedInAtLeastOne: number;
  evaluable: number;
}

export interface RelationshipEconomicInvestigation {
  aligned: RelationshipAlignedBucket[];
  primaryLeaders: RelationshipAlignedBucket[];
  secondaryLeaders: RelationshipAlignedBucket[];
  directions: RelationshipDirectionCounts;
  partialOrUnequalBuckets: number;
}

function readValue(
  point: RelationshipSeriesPoint,
  key: MarginCenterEconomicMetricKey,
): number | null {
  const value = point[key];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : null;
}

function difference(
  current: RelationshipSeriesPoint,
  comparison: RelationshipSeriesPoint,
  key: MarginCenterEconomicMetricKey,
): number | null {
  const a = readValue(current, key);
  const b = readValue(comparison, key);
  if (a === null || b === null) return null;
  return a - b;
}

function coveredDays(point: RelationshipSeriesPoint): number | null {
  const from = Date.parse(`${point.covered_start}T00:00:00Z`);
  const to = Date.parse(`${point.covered_end}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return Math.round((to - from) / 86400000) + 1;
}

function leaders(
  rows: RelationshipAlignedBucket[],
  field: 'primaryDifference' | 'secondaryDifference',
): RelationshipAlignedBucket[] {
  return rows
    .filter((row) => row[field] !== null && Math.abs(row[field] ?? 0) > 1e-9)
    .slice()
    .sort((a, b) =>
      Math.abs(b[field] ?? 0) - Math.abs(a[field] ?? 0)
      || a.index - b.index
    )
    .slice(0, 3);
}

export function buildRelationshipEconomicInvestigation(
  series: RelationshipSeriesPoint[],
  reading: MarginCenterEconomicReading,
): RelationshipEconomicInvestigation {
  const comparisonByIndex = new Map(
    series
      .filter((point) => point.period === 'comparison')
      .map((point) => [point.bucket_index, point] as const),
  );

  const aligned = series
    .filter((point) => point.period === 'current')
    .sort((a, b) => a.bucket_index - b.bucket_index)
    .flatMap((point) => {
      const comparison = comparisonByIndex.get(point.bucket_index);
      if (!comparison) return [];
      return [{
        index: point.bucket_index,
        current: point,
        comparison,
        primaryDifference: difference(point, comparison, reading.primary.key),
        secondaryDifference: difference(point, comparison, reading.secondary.key),
      }];
    });

  const directions: RelationshipDirectionCounts = {
    bothRise: 0,
    bothFall: 0,
    opposite: 0,
    unchangedInAtLeastOne: 0,
    evaluable: 0,
  };

  for (const row of aligned) {
    const a = row.primaryDifference;
    const b = row.secondaryDifference;
    if (a === null || b === null) continue;
    directions.evaluable += 1;
    if (a > 0 && b > 0) directions.bothRise += 1;
    else if (a < 0 && b < 0) directions.bothFall += 1;
    else if ((a > 0 && b < 0) || (a < 0 && b > 0)) directions.opposite += 1;
    else directions.unchangedInAtLeastOne += 1;
  }

  return {
    aligned,
    primaryLeaders: leaders(aligned, 'primaryDifference'),
    secondaryLeaders: leaders(aligned, 'secondaryDifference'),
    directions,
    partialOrUnequalBuckets: aligned.filter((row) =>
      row.current.is_partial_bucket
      || row.comparison.is_partial_bucket
      || coveredDays(row.current) !== coveredDays(row.comparison)
    ).length,
  };
}
